/**
 * RWBR — THE 2025 PRO SEASON, AS A PLACE TO START A CAREER.
 *
 * D-EZ-76. Howard, 21 Aug 2026:
 *
 *   *"Then I want you to load a seed starting scenario for users to be able to
 *   use the 2025 season (drivers and tracks) to start their career association.
 *   Everything behaves as the engine should thereafter regarding driver changes
 *   and retirments and rookies etc. but a 2025 start to a career would be
 *   awesome."*
 *
 * ⚠ THE SCENARIO IS A STARTING POSITION, NOT A MODE. That sentence is the whole
 * specification and it is the part that is easy to get wrong. Everything here
 * runs ONCE, before race 1: it hands `beginSeason` a roster, a charter and a
 * calendar. After the green flag there is no 2025 code left in the loop —
 * transfers, injuries, deaths, rookies, re-rates and the winter all run exactly
 * as they do for a generated field, because they are handed exactly the same
 * kind of object. Season two of a 2025 career is a season, not a re-run.
 *
 * The one thing that does persist is `alternateCards`, and it persists because
 * it is a property of five printed CARDS rather than of the year — see
 * `altCards.ts`. A driver whose card the winter re-rates keeps his alternate's
 * shape, not his 2025 numbers.
 *
 * WHAT IS REAL HERE, and it is worth being precise because the value of a
 * scenario is entirely in this:
 *
 *   • The 45 seats and every number on every card — `roster-2025.ts`, read off
 *     the printed sheet.
 *   • The 36-race calendar in its printed order — `printedSchedule(seed, 2025)`,
 *     rebuilt from the race numbers on the 27 track cards.
 *   • The shops, below, as the garage actually stood.
 *
 * WHAT IS NOT: nothing about the standings, the points, who won or how the year
 * went. A scenario that also decided the results would not be a game.
 *
 * ⚠ THE ALTERNATES DO NOT SURVIVE THE WINTER, and that is a ruling rather than
 * an oversight. `SeasonOptions.alternateCards` is set when the career starts and
 * is not carried into season two. The reason is that the winter RE-RATES every
 * card (`rerate.ts`): after it, the numbers on Shane van Gisbergen's card are
 * the ones a season of racing earned him, not the ones PLAAY printed, and the
 * printed road-course alternate is no longer a description of the same driver.
 * Carrying a fixed overlay past that point would quietly re-apply a 2025 rating
 * to a 2029 driver every time he turned up at Sonoma.
 */

import { age2025, PRIORS_2025 } from '../data/careers2025.ts';
import type { CareerPrior } from './history/types.ts';
import { INKS, liveryFor, type InkName } from './livery.ts';
import { makeRng } from './dice.ts';
import { printedCards, printedSchedule, type SeasonSchedule } from './schedule.ts';
import type { Team } from './field.ts';
import type { DriverCard } from './types.ts';
import { describeDelta, type AlternateCard } from './altCards.ts';
import { baseCard, ROSTER_2025, type Roster2025Card } from '../data/roster-2025.ts';

export const SCENARIO_2025_YEAR = 2025;

/**
 * THE GARAGE AS IT STOOD, by car number.
 *
 * ⚠ BY NUMBER AND NOT BY DRIVER, on purpose. Four of these seats are shared —
 * the set prints three men in the 44 and three in the 66 — and a charter keyed
 * on a driver would have to be rebuilt every time one of them was chosen. The
 * seat is the thing the shop owns; who is sitting in it this year is a separate
 * question, answered by `seatsFor`.
 *
 * The inks are dealt in this order and each shop keeps its own, which is what
 * makes the four Hendrick cars look like four Hendrick cars on the board.
 */
interface Shop2025 {
  teamId: string;
  name: string;
  ink: InkName;
  /** The car numbers this shop fields, as printed on the cards. */
  numbers: readonly string[];
}

export const SHOPS_2025: readonly Shop2025[] = Object.freeze([
  { teamId: 'rw25-hendrick', name: 'Hendrick Motorsports', ink: 'electric', numbers: ['5', '9', '24', '48'] },
  { teamId: 'rw25-gibbs', name: 'Joe Gibbs Racing', ink: 'orange', numbers: ['11', '19', '20', '54'] },
  { teamId: 'rw25-penske', name: 'Team Penske', ink: 'navy', numbers: ['2', '12', '22'] },
  { teamId: 'rw25-childress', name: 'Richard Childress Racing', ink: 'black', numbers: ['3', '8', '33'] },
  { teamId: 'rw25-23xi', name: '23XI Racing', ink: 'magenta', numbers: ['23', '35', '45', '67'] },
  { teamId: 'rw25-trackhouse', name: 'Trackhouse Racing', ink: 'aqua', numbers: ['1', '87', '88', '99'] },
  { teamId: 'rw25-rfk', name: 'RFK Racing', ink: 'red', numbers: ['6', '17', '60'] },
  { teamId: 'rw25-spire', name: 'Spire Motorsports', ink: 'lime', numbers: ['7', '71', '77'] },
  { teamId: 'rw25-legacy', name: 'Legacy Motor Club', ink: 'purple', numbers: ['42', '43', '84'] },
  { teamId: 'rw25-frontrow', name: 'Front Row Motorsports', ink: 'gunmetal', numbers: ['4', '34', '38'] },
  { teamId: 'rw25-kaulig', name: 'Kaulig Racing', ink: 'chrome', numbers: ['10', '16'] },
  { teamId: 'rw25-haas', name: 'Haas Factory Team', ink: 'silver', numbers: ['41'] },
  { teamId: 'rw25-hyak', name: 'Hyak Motorsports', ink: 'kelly', numbers: ['47'] },
  { teamId: 'rw25-wood', name: 'Wood Brothers Racing', ink: 'wine', numbers: ['21'] },
  { teamId: 'rw25-ware', name: 'Rick Ware Racing', ink: 'copper', numbers: ['01', '51'] },
  { teamId: 'rw25-beard', name: 'Beard Motorsports', ink: 'forest', numbers: ['62'] },
  { teamId: 'rw25-livefast', name: 'Live Fast Motorsports', ink: 'sky', numbers: ['78'] },
  { teamId: 'rw25-nyracing', name: 'NY Racing Team', ink: 'cobalt', numbers: ['44'] },
  { teamId: 'rw25-garage66', name: 'Garage 66', ink: 'teal', numbers: ['66'] },
]);

/** Every driver the set prints for one car number, in card order. */
export function candidatesForNumber(carNumber: string): Roster2025Card[] {
  return ROSTER_2025.filter((d) => d.carNumber === carNumber);
}

/**
 * WHO IS IN THE CAR THIS YEAR — one driver per number.
 *
 * The set's own instruction, verbatim: *"Please be sure to choose ONE driver for
 * each team before beginning the Race Week procedure, so as to avoid having
 * driver team-mates participating in the same race."* Four numbers share a seat
 * — the 33, the 44, the 66 and the 78 — and this is where the choice is made.
 *
 * ⚠ THE MAN WITH THE FULL CARD GETS THE SEAT UNLESS THE SEED SAYS OTHERWISE.
 * The set marks the part-timers with an "x" on the hood (`standIn`), and a
 * scenario whose Daytona 500 is a coin flip between Austin Hill and a stand-in
 * is not the 2025 season. So a number with exactly one full-time driver seats
 * him; a number that is stand-ins all the way down draws from the seed, which
 * is the only honest answer when the sheet does not have one.
 */
export function seatsFor(seed: number): Map<string, string> {
  const rng = makeRng((seed ^ 0x20250101) >>> 0);
  const seats = new Map<string, string>();
  const numbers = [...new Set(ROSTER_2025.map((d) => d.carNumber))];
  for (const number of numbers) {
    const all = candidatesForNumber(number);
    const regulars = all.filter((d) => !d.standIn);
    const pool = regulars.length > 0 ? regulars : all;
    // The draw is only ever reached for a seat nobody held all year, but it is
    // still a draw off the seed and not `[0]`, so two careers from two seeds do
    // not both put J.J. Yeley in the 44 for thirty-six straight races.
    const chosen = pool.length === 1 ? pool[0] : pool[Math.floor(rng() * pool.length)];
    seats.set(number, chosen.driverId);
  }
  return seats;
}

export interface Scenario2025 {
  /** The licensed roster — one driver per seat, 45 of them. */
  roster: DriverCard[];
  /** The charter, with liveries, ready for `beginSeason` and the board. */
  teams: Team[];
  /**
   * The five printed alternates, for a screen to show. NOT for the engine —
   * every one of them is already on its own driver's card (`DriverCard.altCard`)
   * and travels with him through the winter. This is a report, not a wiring.
   */
  alternates: { driverId: string; name: string; card: AlternateCard; summary: string }[];
  /** The real 36-race calendar in its printed order. */
  schedule: SeasonSchedule;
  /** The 27 track cards the calendar is built from. */
  trackIds: string[];
  /**
   * D-EZ-97 — careers before the league, for the record book. Empty today: the
   * repository holds no real career statistics and none are invented (see
   * `data/careers2025.ts`).
   */
  priors: Record<string, CareerPrior>;
}

/**
 * Build the whole starting position.
 *
 * Pure and deterministic from the seed: the same number gives the same garage
 * and the same year, which is the promise every other part of this engine makes
 * and the reason a season can be handed to somebody else as one integer.
 */
export function scenario2025(seed: number): Scenario2025 {
  const seats = seatsFor(seed);
  const seated = new Set(seats.values());

  const teamOfNumber = new Map<string, string>();
  for (const shop of SHOPS_2025) for (const n of shop.numbers) teamOfNumber.set(n, shop.teamId);

  /*
    ⚠ EVERY SEAT HAS TO HAVE A SHOP AND EVERY SHOP HAS TO HAVE ITS SEATS.

    Checked rather than assumed, because the failure is invisible: a car number
    missing from `SHOPS_2025` simply becomes an independent, races all year, and
    the only place anybody would notice is the P&L on the Career page, where one
    driver would be quietly missing from his own team's books.
  */
  for (const number of seats.keys()) {
    if (!teamOfNumber.has(number)) throw new Error(`the ${SCENARIO_2025_YEAR} garage has no shop for car #${number}`);
  }
  for (const shop of SHOPS_2025) {
    for (const n of shop.numbers) {
      if (!seats.has(n)) throw new Error(`${shop.name} fields car #${n}, which no ${SCENARIO_2025_YEAR} card prints`);
    }
  }

  const entries = ROSTER_2025.filter((d) => seated.has(d.driverId));
  const roster: DriverCard[] = entries.map((entry) => {
    // D-EZ-93 — real-ish ages for real drivers (approximate birth years).
    const age = age2025(entry.driverId, SCENARIO_2025_YEAR);
    return {
      ...baseCard(entry),
      teamId: teamOfNumber.get(entry.carNumber),
      ...(age !== undefined ? { age } : {}),
    };
  });

  const teams: Team[] = SHOPS_2025.map((shop) => ({
    teamId: shop.teamId,
    name: shop.name,
    driverIds: shop.numbers.map((n) => seats.get(n) as string),
    baseInk: shop.ink,
    livery: liveryFor(shop.teamId, undefined, shop.ink),
  }));

  /*
    THE FIVE ALTERNATES, read back off the cards that now carry them.

    ⚠ NOTHING IS BUILT HERE. `baseCard` attached each one when it built the card,
    as a difference; this only reads them back so a screen can say "Road Courses
    — +2 gas, +3 brake, 2 crew grades better". Building them a second time in a
    second place is how two versions of one rule start to disagree.
  */
  const alternates = roster
    .filter((card) => card.altCard)
    .map((card) => ({
      driverId: card.driverId,
      name: card.name,
      card: card.altCard as AlternateCard,
      summary: describeDelta((card.altCard as AlternateCard).delta),
    }));

  return {
    roster,
    teams,
    alternates,
    schedule: printedSchedule(seed, SCENARIO_2025_YEAR),
    trackIds: printedCards(SCENARIO_2025_YEAR),
    priors: Object.fromEntries(
      Object.entries(PRIORS_2025).filter(([id]) => roster.some((c) => c.driverId === id)),
    ),
  };
}

/** Everything a screen needs to offer the scenario without building it. */
export const SCENARIO_2025_BLURB = {
  id: 'pro-2025',
  year: SCENARIO_2025_YEAR,
  title: '2025 Pro Season',
  seats: [...new Set(ROSTER_2025.map((d) => d.carNumber))].length,
  shops: SHOPS_2025.length,
  opener: 'Daytona 500',
  finale: 'Cup Series Championship, Phoenix',
  /** Named so a reader can see the ink is not decoration. */
  note:
    'The real 45 seats, the real garage and the real 36-race calendar in its printed order. ' +
    'Everything after the green flag is the ordinary engine.',
} as const;

/** A sanity check anything may run: the inks are real and no shop shares one. */
export function scenarioInkCheck(): void {
  const seen = new Set<string>();
  for (const shop of SHOPS_2025) {
    if (!(shop.ink in INKS)) throw new Error(`${shop.name} is painted "${shop.ink}", which is not an ink`);
    if (seen.has(shop.ink)) throw new Error(`two shops are painted ${shop.ink}`);
    seen.add(shop.ink);
  }
}
